import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';

export const NONE_VALUE = 'NONE';

export const COUNTRY_OPTIONS = [
  { field: EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_RUSSIA, value: 'russia' },
  { field: EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SAUDI_ARABIA, value: 'saudiArabia' },
  { field: EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_IRAN, value: 'iran' },
  { field: EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_IRAQ, value: 'iraq' },
  { field: EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SUDAN, value: 'sudan' },
  { field: EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_LEBANON, value: 'lebanon' },
  { field: EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SYRIA, value: 'syria' },
] as const;

export const NONE_FIELD = EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_NONE;

export const useSanctionedCountriesLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');

  const storedArray =
    (getDefaultValueByQuestionId(EddEmbeddedFields.EDD_SANCTIONED_COUNTRIES) as string[]) || [];

  const formDefaultValues = {
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_RUSSIA]: storedArray.includes('russia'),
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SAUDI_ARABIA]: storedArray.includes('saudiArabia'),
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_IRAN]: storedArray.includes('iran'),
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_IRAQ]: storedArray.includes('iraq'),
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SUDAN]: storedArray.includes('sudan'),
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_LEBANON]: storedArray.includes('lebanon'),
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SYRIA]: storedArray.includes('syria'),
    [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_NONE]: storedArray.includes(NONE_VALUE),
  };

  const formSchema = z
    .object({
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_RUSSIA]: z.boolean().default(false),
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SAUDI_ARABIA]: z.boolean().default(false),
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_IRAN]: z.boolean().default(false),
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_IRAQ]: z.boolean().default(false),
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SUDAN]: z.boolean().default(false),
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_LEBANON]: z.boolean().default(false),
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_SYRIA]: z.boolean().default(false),
      [EddEmbeddedFields.EDD_SANCTIONED_COUNTRY_NONE]: z.boolean().default(false),
    })
    .superRefine((data, ctx) => {
      const anySelected = COUNTRY_OPTIONS.some((o) => data[o.field]) || data[NONE_FIELD];
      if (!anySelected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('organic.sanctionedCountries.validation.selectAtLeastOne'),
          path: [COUNTRY_OPTIONS[0].field],
        });
      }
    });

  const formMethods = useFormHook({
    schemaTitle: 'sanctioned-countries',
    formDefaultValues,
    formSchema,
  });

  const handleSubmit = async () => {
    const selected: string[] = [];
    COUNTRY_OPTIONS.forEach((o) => {
      if (formMethods.getValues(o.field)) selected.push(o.value);
    });
    if (formMethods.getValues(NONE_FIELD)) selected.push(NONE_VALUE);

    saveEddQuestionToLocalStore({
      question: t('organic.sanctionedCountries.title'),
      questionId: EddEmbeddedFields.EDD_SANCTIONED_COUNTRIES,
      answer: JSON.stringify(selected),
    });

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_SANCTIONED_COUNTRIES);
    } catch (error) {
      reportError({
        severity: ObservabilityErrorSeverity.ERROR,
        metadata: {
          name: 'EddSanctionedCountriesSubmitError',
          message: error instanceof Error ? error.message : String(error),
        },
        groupingHash: 'edd-submit-error',
      });
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
